import { formConvertErrorMessage } from "helpers/form_helper";
import { errorMessage } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { useToasts } from "react-toast-notifications";
import orderScanService from "services/orderScanService";

export const useOrderScanRemove = ({ onSuccess, onError }) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useMutation(orderScanService.removeScan, {
    retry: 0,
    onSuccess: (response, variable) => {
      const { result, message, data } = response?.data;
      const mess = `message.orderScanRemove`;
      if (result) {
        addToast(
          t(mess, {
            target: t("order"),
            status: t("message.success"),
          }),
          {
            appearance: "success",
            autoDismiss: true,
          }
        );
        if (typeof onSuccess === "function") {
          onSuccess(data);
        }
      } else {
        addToast(
          t(mess, {
            target: t("order"),
            status: t("message.error"),
          }),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (message) {
          addToast(message, {
            appearance: "error",
            autoDismiss: true,
          });
        }

        if (typeof onError === "function") {
          onError(message);
        }
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

function parseScanStartStatusResponse(response) {
  const body = response?.data ?? {};
  const payload =
    body?.data && typeof body.data === "object" && !Array.isArray(body.data)
      ? body.data
      : body;

  const canStartRaw = payload?.can_start ?? body?.can_start;
  const scanIdRaw = payload?.scan_id ?? payload?.id ?? body?.scan_id;

  const canStart =
    canStartRaw === false ||
    canStartRaw === 0 ||
    canStartRaw === "0" ||
    canStartRaw === "false"
      ? false
      : true;

  return {
    canStart,
    scanId:
      scanIdRaw != null && scanIdRaw !== "" ? String(scanIdRaw) : null,
  };
}

export const useScanStartStatus = (params, { enabled = true } = {}) => {
  return useQuery(
    [
      "scan-start-status",
      params?.isSendOrder,
      params?.allowed_group_to_transfer,
      params?.hub_id,
      params?.from_hub,
      params?.to_hub,
    ],
    async () => {
      const response = await orderScanService.startStatus(params);
      const { result, message } = response?.data ?? {};

      if (!result) {
        throw new Error(message || "Failed to fetch scan start status");
      }

      return parseScanStartStatusResponse(response);
    },
    {
      enabled: enabled && !!params?.hub_id,
      staleTime: 0,
      cacheTime: 0,
      retry: 0,
      keepPreviousData: false,
      refetchOnWindowFocus: false,
    }
  );
};

export const useOrderScanStart = ({ onSuccess, onError }) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useMutation(orderScanService.start, {
    retry: 0,
    onSuccess: (response, variable) => {
      const { result, message, data } = response?.data;
      const mess = `message.${
        variable.isSendOrder ? "orderScanStartSend" : "orderScanStartReceive"
      }`;
      if (result) {
        addToast(
          t(mess, {
            target: t("order"),
            status: t("message.success"),
          }),
          {
            appearance: "success",
            autoDismiss: true,
          }
        );
        if (typeof onSuccess === "function") {
          onSuccess(parseScanStartStatusResponse(response));
        }
      } else {
        addToast(
          t(mess, {
            target: t("order"),
            status: t("message.error"),
          }),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (message) {
          addToast(message, {
            appearance: "error",
            autoDismiss: true,
          });
        }

        if (typeof onError === "function") {
          onError(message);
        }
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useOrderScanEnd = ({ onSuccess, onError }) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useMutation(orderScanService.end, {
    retry: 0,
    onSuccess: (response, variable) => {
      const { result, message, data } = response?.data;
      if (result) {
        addToast(message, {
          appearance: "success",
          autoDismiss: true,
        });
        if (typeof onSuccess === "function") {
          onSuccess(data);
        }
      } else {
        addToast(
          t(`message.orderScanEnd`, {
            target: t("order"),
            status: t("message.error"),
          }),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (message) {
          addToast(message, {
            appearance: "error",
            autoDismiss: true,
          });
        }

        if (typeof onError === "function") {
          onError(message);
        }
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useOrderScanReceive = ({ onSuccess, onError }) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useMutation(orderScanService.receivce, {
    retry: 0,
    onSuccess: (response, variable) => {
      const { result, message, data } = response?.data;
      if (result) {
        addToast(message, {
          appearance: "success",
          autoDismiss: true,
        });
        if (typeof onSuccess === "function") {
          onSuccess(data);
        }
      } else {
        addToast(
          t(
            `message.${
              variable.isSendOrder ? "orderScanSend" : "orderScanReceive"
            }`,
            {
              target: t("order"),
              status: t("message.error"),
            }
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (message) {
          addToast(message, {
            appearance: "error",
            autoDismiss: true,
          });
        }

        if (typeof onError === "function") {
          onError(message);
        }
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useOrderScanInfo = ({ onSuccess, onError }) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useMutation(orderScanService.info, {
    retry: 0,
    onSuccess: (response, variable) => {
      const { result, message, data } = response?.data;
      if (result) {
        addToast(message, {
          appearance: "success",
          autoDismiss: true,
        });
        if (typeof onSuccess === "function") {
          onSuccess({ ...data, scan_code: variable.barcode });
        }
      } else {
        addToast(
          t("message.orderScanInfo", {
            target: t("order"),
            status: t("message.error"),
          }),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (message) {
          addToast(message, {
            appearance: "error",
            autoDismiss: true,
          });
        }

        if (typeof onError === "function") {
          onError(message);
        }
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useOrderScanAdd = ({ onSuccess, onError }) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useMutation(orderScanService.addScan, {
    retry: 0,
    onSuccess: (response, variables) => {
      const { result, message, data } = response?.data;
      if (result) {
        addToast(message, {
          appearance: "success",
          autoDismiss: true,
        });
        if (typeof onSuccess === "function") {
          onSuccess(data);
        }
      } else {
        addToast(
          t("message.orderAddScan", {
            target: t("order"),
            status: t("message.error"),
          }),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (message) {
          addToast(message, {
            appearance: "error",
            autoDismiss: true,
          });
        }

        if (typeof onError === "function") {
          onError({
            errorMessage: message,
            code: variables.code,
          });
        }
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useOrderScanUploadFile = ({ onSuccess, onError }) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useMutation(orderScanService.uploadFile, {
    retry: 0,
    onSuccess: response => {
      const { result, message, data } = response?.data;
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
      if (!result) {
        addToast(message, {
          appearance: "error",
          autoDismiss: true,
        });
      }
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useOrderScanRemoveFile = ({ onSuccess, onError }) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useMutation(orderScanService.removeFile, {
    retry: 0,
    onSuccess: response => {
      const { result, message, data } = response?.data;
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
      if (!result) {
        addToast(message, {
          appearance: "error",
          autoDismiss: true,
        });
      }
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// ListOrderScan
export const useListOrderScan = (payload, onSuccess, onError) => {
  const { t } = useTranslation(["order"]);
  const { addToast } = useToasts();

  const { params } = payload;

  return useQuery(
    ["list-order-scan", params?.page],
    () => orderScanService.listOrderScan(payload),
    {
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.list", {
                target: t("order-scan"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return {
          items: data.items || [],
          params: params,
          totalCount: data._meta ? data._meta.totalCount : data.items.length,
        };
      },
    }
  );
};

// List OrderScanStatus
export const useListOrderScanStatus = (payload, onSuccess, onError) => {
  const { t } = useTranslation(["bus"]);
  const { addToast } = useToasts();

  const { params } = payload;

  return useQuery(
    ["order-scan-status", params?.page],
    () => orderScanService.listOrderScanStatus(payload),
    {
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.list", {
                target: t("orderScanStatus"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return data;
      },
    }
  );
};

// List OrderScanActionType
export const useListOrderScanActionType = (payload, onSuccess, onError) => {
  const { t } = useTranslation(["bus"]);
  const { addToast } = useToasts();

  const { params } = payload;

  return useQuery(
    ["order-scan-action-type", params?.page],
    () => orderScanService.listOrderScanActionType(payload),
    {
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.list", {
                target: t("orderScanActionType"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return data;
      },
    }
  );
};

// ListPhaseOrderScan
export const useListPhaseOrderScan = (payload, onSuccess, onError) => {
  const { t } = useTranslation(["order"]);
  const { addToast } = useToasts();

  const { params } = payload;

  return useQuery(
    ["list-phase-order-scan", params?.page],
    () => orderScanService.listPhaseOrderScan(payload),
    {
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.list", {
                target: t("phase-order-scan"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return {
          items: data.items || [],
          params: params,
          totalCount: data._meta ? data._meta.totalCount : data.items.length,
        };
      },
    }
  );
};

// List PhaseOrderScanStatus
export const useListPhaseOrderScanStatus = (payload, onSuccess, onError) => {
  const { t } = useTranslation(["bus"]);
  const { addToast } = useToasts();

  const { params } = payload;

  return useQuery(
    ["phase-order-scan-status", params?.page],
    () => orderScanService.listPhaseOrderScanStatus(payload),
    {
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.list", {
                target: t("orderScanStatus"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return data;
      },
    }
  );
};

// List PhaseOrderScanActionType
export const useListPhaseOrderScanActionType = (
  payload,
  onSuccess,
  onError
) => {
  const { t } = useTranslation(["bus"]);
  const { addToast } = useToasts();

  const { params } = payload;

  return useQuery(
    ["phase-order-scan-action-type", params?.page],
    () => orderScanService.listPhaseOrderScanActionType(payload),
    {
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.list", {
                target: t("orderScanActionType"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return data;
      },
    }
  );
};

export const useDetailPhaseOrder = (payload, onSuccess, onError) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["orders"]);

  return useQuery(
    ["phase-order-detail"],
    () => orderScanService.detailPhaseOrderScan(payload),
    {
      enabled: false,
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.fetch_profile", {
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      // initialData: Object({ data: {} }),
      select: response => {
        const { data } = response;
        return data;
      },
    }
  );
};

export const useDashboardPhaseOrderScan = ({ onSuccess, onError }) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useMutation(orderScanService.dashboardPhaseOrderScan, {
    retry: 0,
    onSuccess: (response, variable) => {
      const { data } = response;
      const mess = `message.dashboardPhaseOrderScan`;
      if (data) {
        // addToast(
        //   t(mess, {
        //     target: t("order"),
        //     status: t("message.success"),
        //   }),
        //   {
        //     appearance: "success",
        //     autoDismiss: true,
        //   }
        // );
        if (typeof onSuccess === "function") {
          onSuccess(data);
        }
      } else {
        addToast(
          t(mess, {
            target: t("order"),
            status: t("message.error"),
          }),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (message) {
          addToast(message, {
            appearance: "error",
            autoDismiss: true,
          });
        }

        if (typeof onError === "function") {
          onError(message);
        }
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

export const useCheckOrderAddToOrderTransfer = ({ onSuccess, onError }) => {
  const { addToast } = useToasts();
  const { t } = useTranslation(["order"]);

  return useMutation(orderScanService.checkOrderAddToOrderTransfer, {
    retry: 0,
    onSuccess: (response, variables) => {
      const { result, message, data } = response?.data;
      if (result) {
        addToast(
          t("message.checkOrderAddToOrderTransfer", {
            target: t("order"),
            status: t("message.success"),
          }),
          {
            appearance: "success",
            autoDismiss: true,
          }
        );
        if (typeof onSuccess === "function") {
          onSuccess(data);
        }
      } else {
        addToast(
          t("message.checkOrderAddToOrderTransfer", {
            target: t("order"),
            status: t("message.error"),
          }),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (message) {
          addToast(message, {
            appearance: "error",
            autoDismiss: true,
          });
        }

        if (typeof onError === "function") {
          onError({
            errorMessage: message,
            code: variables.orderCode,
          });
        }
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};
